import {useState, useEffect, useCallback, useRef} from 'react'

/**
 * @name useScrollAtTop
 *
 * @description
 * Custom hook that detects if the window is scrolled to the top.
 *
 * @hook
 * @returns {boolean} A boolean value indicating whether the window is scrolled to the top.
 *
 * @example
 * ```jsx
 * const MyComponent = () => {
 *  const isAtTop = useScrollAtTop();
 *
 *  return (
 *  <div>
 *  {isAtTop ? 'You are at the top!' : 'You are not at the top.'}
 *  </div>
 *  );
 * };
 * ```
 */
interface AnimatedSlideStateDetail {
    active: boolean
    isFirstSlide: boolean
    scrollOwner: 'swiper' | 'footer'
}

const getScrollTop = (): number => {
    if (typeof window === 'undefined') return 0

    return Math.max(window.scrollY || 0, window.pageYOffset || 0, document.documentElement?.scrollTop || 0, document.body?.scrollTop || 0)
}

const useScrollAtTop = (): boolean => {
    const [isAtTop, setIsAtTop] = useState(true)
    const animatedSlideStateRef = useRef<AnimatedSlideStateDetail | null>(null)

    const handleScroll = useCallback(() => {
        const animatedSlideState = animatedSlideStateRef.current
        const isPhysicalTop = getScrollTop() <= 0
        const isAnimatedSlideTop = !animatedSlideState?.active || animatedSlideState.isFirstSlide

        setIsAtTop(isPhysicalTop && isAnimatedSlideTop)
    }, [])

    useEffect(() => {
        if (typeof window === 'undefined') return

        const handleAnimatedSlideStateChange = (event: Event) => {
            animatedSlideStateRef.current = (event as CustomEvent<AnimatedSlideStateDetail>).detail
            handleScroll()
        }

        handleScroll()

        window.addEventListener('scroll', handleScroll, {passive: true})
        window.addEventListener('resize', handleScroll)
        window.addEventListener('animated-slide-scroll-sync', handleScroll)
        window.addEventListener('animated-slide-state-change', handleAnimatedSlideStateChange)

        return () => {
            window.removeEventListener('scroll', handleScroll)
            window.removeEventListener('resize', handleScroll)
            window.removeEventListener('animated-slide-scroll-sync', handleScroll)
            window.removeEventListener('animated-slide-state-change', handleAnimatedSlideStateChange)
        }
    }, [handleScroll])

    return isAtTop
}

export {useScrollAtTop}
